// auth.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { RoutesPath } from '../../shared/constants/routes-path';
import { environment } from '../../../environments/environment';
import { Observable, tap } from 'rxjs';

type TokenResponse = {
  access_token: string;
  refresh_token?: string;
  scope?: string;
  id_token?: string;
  token_type?: string;
  expires_in?: number;
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private refreshTimeoutId: number | null = null;
  private readonly redirectUrlStorageKey = 'auth_redirect_url';

  // This method is used to initialize the class instance using router
  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    this.scheduleTokenRefresh();
  }

  // This method is used to log in
  login() {
    // Intentionally left blank for direct-login flow.
  }

  // This method is used to log out
  logout() {
    // Save selected language
    const lang = localStorage.getItem("selectedLang");
    // Clear storages
    sessionStorage.clear();
    localStorage.clear();
    // Restore language
    if (lang) {
      localStorage.setItem("selectedLang", lang);
    }
    this.router.navigate(['/', RoutesPath.PUBLIC.LOGIN]);
    this.clearRefreshTimer();
  }

  // This method is used to check if logged in
  isLoggedIn(): boolean {
    return Boolean(this.getStoredAccessToken());
  }

  // This method is used to get access token
  getAccessToken(): string | null {
    return this.getStoredAccessToken();
  }

  // This method is used to get access token expiration
  getAccessTokenExpiresAt(): number | null {
    return this.getStoredExpiresAt();
  }

  // This method is used to get refresh token
  getRefreshToken(): string | null {
    return this.getStoredToken('refresh_token');
  }

  // This method is used to get id token
  getIdToken(): string | null {
    return this.getStoredToken('id_token');
  }

  // This method is used to get user claims
  getUserClaims(): any {
    return this.getStoredUser()?.user || null;
  }

  // This method is used to refresh token
  refreshToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return new Observable<never>((subscriber) => subscriber.complete());
    }
    const redirectUri = window.location.origin;
    const url = `${environment.wso2APIURL}DCAuthToken/AuthTokenREST/renewToken`;
    return this.http.post<TokenResponse>(url, {
      redirect_uri: redirectUri,
      refresh_token: refreshToken
    }).pipe(
      tap((response) => {
        const expiresIn = Number(response?.expires_in || 0);
        const expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : null;
        const existing = this.getStoredUser() || {};
        const updated = {
          ...existing,
          access_token: response.access_token,
          refresh_token: response.refresh_token || existing.refresh_token,
          id_token: response.id_token || existing.id_token,
          token_type: response.token_type || existing.token_type,
          scope: response.scope || existing.scope,
          expiresAt
        };
        localStorage.setItem('login_user', JSON.stringify(updated));
        this.scheduleTokenRefresh();
      })
    );
  }

  setRedirectUrl(url: string) {
    localStorage.setItem(this.redirectUrlStorageKey, url);
  }

  getRedirectUrl(): string | null {
    return localStorage.getItem(this.redirectUrlStorageKey);
  }

  consumeRedirectUrl(): string | null {
    const redirectUrl = this.getRedirectUrl();
    if (redirectUrl) {
      localStorage.removeItem(this.redirectUrlStorageKey);
    }
    return redirectUrl;
  }

  // This method is used to update user store using data
  updateUserStore(data: any) {
    localStorage.setItem('login_user', JSON.stringify(data));
    this.scheduleTokenRefresh();
  }

  private getStoredAccessToken(): string | null {
    const token = this.getStoredToken('access_token');
    if (!token) {
      return null;
    }
    const expiresAt = this.getStoredExpiresAt();
    if (expiresAt && expiresAt <= Date.now()) {
      return null;
    }
    return token;
  }

  private getStoredToken(key: 'access_token' | 'refresh_token' | 'id_token'): string | null {
    const user = this.getStoredUser();
    return user?.[key] || null;
  }

  private getStoredExpiresAt(): number | null {
    const user = this.getStoredUser();
    const expiresAt = Number(user?.expiresAt || 0);
    return Number.isFinite(expiresAt) && expiresAt > 0 ? expiresAt : null;
  }

  private getStoredUser(): any {
    const raw = localStorage.getItem('login_user');
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private scheduleTokenRefresh(): void {
    this.clearRefreshTimer();
    const refreshToken = this.getRefreshToken();
    const expiresAt = this.getStoredExpiresAt();
    if (!refreshToken || !expiresAt) {
      return;
    }
    const now = Date.now();
    const refreshAt = Math.max(expiresAt - 60_000, now + 5_000);
    const delay = refreshAt - now;
    this.refreshTimeoutId = window.setTimeout(() => {
      this.refreshToken().subscribe({
        complete: () => {
          this.scheduleTokenRefresh();
        },
        error: () => {
          this.scheduleTokenRefresh();
        }
      });
    }, delay);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimeoutId !== null) {
      window.clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
  }
}
